import { CopyToClipboardBtn } from '@va/dashboard/ui/components';
import { CheckMarkWithCircle, CopyIcon2, ExternalLinkIcon, VerticalThreeDotIcon, VideoCameraIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button, ButtonShapes, Paragraph, paragraphSizes } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { isWixMiniApp } from '@va/util/helpers';
import { useCheckOverflow } from '@va/util/hooks';
import classNames from 'classnames';
import { PropsWithChildren, useRef, useState } from 'react';
import { InternalUrlActions } from './InternalUrlActions';
import { PageUrlAction } from './PageUrlAction';
import { usePageUrlActions } from './usePageUrlActions';

type InteractiveUrlProps = {
  pageUrl: string;
  className?: string;
  isExternal?: boolean;
  onTooltipOpen?: (open: boolean) => void;
};

export const InteractivePageUrl = ({ pageUrl, className, isExternal, onTooltipOpen }: InteractiveUrlProps) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const isOverflowing = useCheckOverflow(ref);

  return (
    <div className='cursor-pointer group/link flex items-center gap-2 overflow-hidden'>
      <TooltipWrapper content={pageUrl} disabled={!isOverflowing}>
        <a
          ref={ref}
          href={pageUrl}
          target='_blank'
          rel='noreferrer'
          className={classNames(
            'text-primary text-12 overflow-hidden truncate hover:underline min-w-[3.5rem]',
            className,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {pageUrl}
        </a>
      </TooltipWrapper>
      <PageUrlActionsTooltip pageUrl={pageUrl} isExternal={isExternal} onTooltipOpen={onTooltipOpen}>
        <Button
          color='primary'
          shape={ButtonShapes.circle}
          className='h-5 w-5 !p-0 invisible sm:visible transition-0 group-hover/link:visible'
          icon={() => <VerticalThreeDotIcon className='w-4 h-4' color='#FFF' />}
          onClick={(e) => e.stopPropagation()}
        />
      </PageUrlActionsTooltip>
    </div>
  );
};

const PageUrlActionsTooltip = ({
  pageUrl,
  isExternal,
  onTooltipOpen,
  children,
}: PropsWithChildren<InteractiveUrlProps>) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <TooltipWrapper
      useDefaultStyle={false}
      content={<PageUrlActionsTooltipContent pageUrl={pageUrl} isExternal={isExternal} />}
      trigger='click'
      onOpenChange={(open) => {
        setIsOpen(open);
        onTooltipOpen?.(open);
      }}
      open={isOpen}
    >
      {children}
    </TooltipWrapper>
  );
};

const PageUrlActionsTooltipContent = ({ pageUrl, isExternal }: InteractiveUrlProps) => {
  const translate = useTranslate();
  const { openInNewTab, viewRecordings } = usePageUrlActions(pageUrl);

  return (
    <div
      className='flex gap-1u items-start sm:flex-col-reverse sm:items-stretch sm:max-w-[20rem] overflow-hidden'
      onClick={(e) => e.stopPropagation()}
    >
      <div className='bg-black-lighter-84 backdrop-blur-sm p-4 rounded-12 rounded-tr-none sm:rounded-t-none max-w-[20rem] max-h-[24rem] overflow-hidden flex'>
        <div className='overflow-auto scrollbar scrollbar-thumb'>
          <Paragraph colorClassName='text-white' size={paragraphSizes.tiny}>
            {pageUrl}
          </Paragraph>
        </div>
      </div>
      <div className='flex flex-col sm:flex-col-reverse gap-1u rounded-12 rounded-tl-none sm:rounded-tl-12 sm:rounded-b-none overflow-hidden'>
        <div className='flex gap-1u w-full'>
          <PageUrlAction
            className='flex-1 !p-0 backdrop-blur-sm'
            onClick={openInNewTab}
            tooltip={translate('pageUrlAction.openInNewTab.tooltip')}
          >
            <ExternalLinkIcon color='#FFFFFFBF' />
          </PageUrlAction>
          <CopyToClipboardBtn
            containerClassName='flex-1'
            buttonProps={{
              className: 'w-full rounded-none !bg-black-lighter-84 backdrop-blur-sm',
              tooltip: translate('pageUrlAction.copy.tooltip'),
            }}
            icon={() => <CopyIcon2 color='#FFFFFFBF' />}
            onCopyIcon={() => <CheckMarkWithCircle color='#FFFFFFBF' />}
            content={pageUrl}
          />
        </div>
        <div className='flex gap-1u'>
          {!isWixMiniApp() && !isExternal && <InternalUrlActions pageUrl={pageUrl} />}
          {isWixMiniApp() && (
            <PageUrlAction onClick={viewRecordings} tooltip={translate('pageUrlAction.viewRecordings.tooltip')}>
              <VideoCameraIcon color='#FFFFFFBF' />
            </PageUrlAction>
          )}
        </div>
      </div>
    </div>
  );
};
